import {
  ApiSupportTicket,
  ApiSupportTicketCategory,
  ApiSupportTicketRequest,
} from '../../types/apiTypes';
import {
  SupportCreateTicket,
  SupportTicket,
  SupportTicketCategory,
} from '../../types/commonTypes';
import { sanitizeValue } from '../../lib/sanitize';

export const hasSupportId = (id: unknown): id is string =>
  typeof id === 'string' && id.trim().length > 0;

export const mapCategories = (
  categories: ApiSupportTicketCategory[] | null
): SupportTicketCategory[] =>
  (categories ?? []).flatMap((category) =>
    hasSupportId(category.ID)
      ? [
          {
            id: category.ID,
            name: category.Name ?? category.ID,
            description: category.Description ?? '',
          },
        ]
      : []
  );

export const mapNewTicket = (
  ticket: SupportCreateTicket
): ApiSupportTicketRequest => {
  if (!hasSupportId(ticket.category))
    throw new Error('Select a valid category.');
  const attachment = ticket.attachment;
  if (attachment && typeof attachment.base64 !== 'string') {
    throw new Error(
      'The attachment could not be read. Please attach it again.'
    );
  }
  return {
    Attachment: attachment
      ? { FileName: attachment.filename, Base64: attachment.base64 as string }
      : null,
    // IDs must be submitted exactly as returned; do not sanitize or coerce them.
    Category: ticket.category,
    Cc: sanitizeValue(ticket.cc),
    Name: sanitizeValue(ticket.name),
    Phone: sanitizeValue(ticket.phone),
    Subcategories: [],
    Subject: sanitizeValue(ticket.subject),
    Text: sanitizeValue(ticket.text),
  };
};

const formatDate = (value?: string) =>
  value && !Number.isNaN(Date.parse(value))
    ? new Date(value).toLocaleString()
    : 'N/A';

export const mapTicket = (ticket: ApiSupportTicket): SupportTicket | null => {
  if (!hasSupportId(ticket.ID)) return null;
  const updates = ticket.Updates ?? [];
  const lastUpdate = updates[updates.length - 1];
  return {
    id: ticket.ID,
    categoryName: ticket.CategoryName ?? 'Uncategorized',
    createdAt: formatDate(ticket.CreatedAt),
    customFields: (ticket.CustomFields ?? []).map((field) => ({
      name: field.Name ?? '',
      value: field.Value,
    })),
    displayID: ticket.DisplayID ?? ticket.ID,
    firstMessage: ticket.FirstMessage ?? '',
    priority: ticket.Priority ?? 'N/A',
    behavior: ticket.Behavior ?? 'N/A',
    subject: ticket.Subject ?? '(No subject)',
    updates: updates.map((update) => ({
      by: update.By || 'Unknown sender',
      timestamp: formatDate(update.Timestamp),
      type: update.Type ?? '',
      message: {
        attachments: (update.Message?.Attachments ?? []).flatMap((attachment) =>
          hasSupportId(attachment.ID)
            ? [
                {
                  id: attachment.ID,
                  filename: attachment.Filename || 'Attachment',
                  url: attachment.Url ?? '',
                },
              ]
            : []
        ),
        ccList: update.Message?.CcList ?? '',
        html: update.Message?.Html ?? '',
        subject: update.Message?.Subject ?? '',
      },
    })),
    lastUpdatedAt: formatDate(lastUpdate?.Timestamp ?? ticket.CreatedAt),
    lastUpdatedBy:
      lastUpdate?.Type?.toLowerCase() === 'staff'
        ? 'TG Support'
        : lastUpdate?.By || 'N/A',
    user: ticket.User ?? 'Unknown sender',
  };
};

export const ticketListQuery = (
  sector: string,
  pending: boolean,
  start: Date,
  end: Date,
  siteId?: string | null,
  page?: string
) => {
  const query = new URLSearchParams({ pending: String(pending) });
  if (!pending) {
    // The date picker represents calendar days in the user's local timezone.
    const first = new Date(start.toISOString());
    const last = new Date(end.toISOString());
    first.setHours(0, 0, 0, 0);
    last.setHours(23, 59, 59, 999);
    query.set('startDate', first.toISOString());
    query.set('endDate', last.toISOString());
  }
  if (sector === 'Hospitality' && siteId && siteId.toLowerCase() !== 'all')
    query.set('accountSiteId', siteId);
  if (page) query.set('page', page);
  return query.toString();
};
